import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { labelTextColor } from '../../utils/labelColor';
import { formatDisplayDateTime } from '../../utils/format';
import SetupProjectsModal from './SetupProjectsModal';
import { leadActivityAt, leadActivityAgo } from './leadActivity';
import { primaryContact } from '../../services/api/leads';
import { formatMonthlyPrice } from './LeadSlaPricingPicker';
import type { Lead } from '../../services/api/leads';
import { LEGACY_CZ_NUMBER_LOCALE, numberFormatter } from '../../utils/localeFormat';

interface LeadCardProps {
  lead: Lead;
  isStale?: boolean;
  staleDays?: number;
  dealValueCZK?: number;
  onClick: (lead: Lead) => void;
  // Phase 105 Plan 105-08 — see the docblock on LeadKanbanBoard.tsx for the
  // full rationale. Defaults to 'internal' so every existing call site is
  // unchanged.
  audience?: 'internal' | 'external';
  /**
   * MKS-347 — an internal viewer who may not write (marketing). The card is
   * not draggable and not a click target: every internal destination behind
   * it (the edit modal, "Set up projects") is a write this role would be
   * 403'd for, so it is not offered. The content itself is unchanged — read
   * access to leads is exactly what the role is for.
   */
  readOnly?: boolean;
}

function formatCurrency(value: string | null): string {
  if (!value || value === '0.00') return '-';
  const num = parseFloat(value);
  return numberFormatter({ style: 'currency', currency: 'CZK', maximumFractionDigits: 0 }, LEGACY_CZ_NUMBER_LOCALE).format(num);
}

function ownerName(owner: Lead['owner']): string {
  if (owner.lastName && owner.firstName) return `${owner.lastName} ${owner.firstName}`;
  return owner.firstName || owner.lastName || 'Unknown';
}

/**
 * Won-lead project status area (WLP-02/WLP-03). Replaces the old two-button
 * Create Project / Link Existing picker (which fought dnd-kit drag handlers
 * and only handled the billable half) with:
 *   - nothing linked  -> a single "Set up projects" button
 *   - one half linked -> a status row for the linked half + a quiet chip to
 *     add the missing half
 *   - both linked     -> two status rows, no button/chip
 * All paths open the same SetupProjectsModal, which links-or-creates both
 * halves in one save.
 */
function WonProjectStatus({ lead }: { lead: Lead }) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const hasBillable = !!lead.convertedProject;
  const hasNonBillable = !!lead.nonBillableProject;

  // `?setupProjects={leadId}` deep-link — the full project form returns here
  // on submit/cancel so the modal reopens where the user left off. Param is
  // cleared immediately (replace) so closing the modal doesn't re-open it and
  // the back button doesn't pogo (mirrors the kanban `openTask` pattern).
  useEffect(() => {
    if (searchParams.get('setupProjects') !== String(lead.id)) return;
    setIsModalOpen(true);
    setSearchParams(
      (prev) => {
        prev.delete('setupProjects');
        return prev;
      },
      { replace: true },
    );
  }, [searchParams, setSearchParams, lead.id]);

  const openModal = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsModalOpen(true);
  };

  return (
    <div className="mt-2 space-y-1">
      {!hasBillable && !hasNonBillable && (
        <button
          onClick={openModal}
          className="w-full rounded bg-green-50 px-2 py-1 text-xs font-medium text-green-700 hover:bg-green-100 transition-colors"
        >
          Set up projects
        </button>
      )}

      {hasBillable && (
        <a
          href={`/projects/${lead.convertedProject!.id}`}
          onClick={(e) => e.stopPropagation()}
          className="flex items-center gap-1 text-xs text-primary-600 hover:text-primary-700"
        >
          Project: {lead.convertedProject!.name || `#${lead.convertedProject!.id}`}
        </a>
      )}
      {!hasBillable && hasNonBillable && (
        <button
          onClick={openModal}
          className="inline-flex items-center gap-1 text-[11px] text-gray-400 hover:text-gray-600"
        >
          &#8853; Billable project
        </button>
      )}

      {hasNonBillable && (
        <a
          href={`/projects/${lead.nonBillableProject!.id}`}
          onClick={(e) => e.stopPropagation()}
          className="flex items-center gap-1 text-xs text-gray-600 hover:text-gray-800"
        >
          Non-billable: {lead.nonBillableProject!.name || `#${lead.nonBillableProject!.id}`}
        </a>
      )}
      {hasBillable && !hasNonBillable && (
        <button
          onClick={openModal}
          className="inline-flex items-center gap-1 text-[11px] text-gray-400 hover:text-gray-600"
        >
          &#8853; Non-billable project
        </button>
      )}

      {isModalOpen && (
        <SetupProjectsModal lead={lead} isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
      )}
    </div>
  );
}

export default function LeadCard({
  lead,
  isStale,
  dealValueCZK,
  onClick,
  audience = 'internal',
  readOnly = false,
}: LeadCardProps) {
  const isExternal = audience === 'external';
  // MKS-347 — the two no-write audiences, collapsed into one question.
  const isInteractive = !isExternal && !readOnly;
  // MKS-344 — the card names the primary contact, so a board scan answers
  // "who is this deal with?" without opening the lead. Every audience,
  // external included (MKS-356): the external payload carries `contacts` and
  // the external detail page already lists them.
  const contact = primaryContact(lead);
  // MKS-346 — the price of the SLA package(s) priced for this deal, so a board
  // scan answers "what are we quoting?" without opening the lead. Data-driven
  // for every audience: a lead with no links renders no badge.
  const slaConfigurations = lead.slaPricingConfigurations ?? [];
  const activityAt = leadActivityAt(lead);
  // Empty for an unusable timestamp, which is why every render below guards
  // on it rather than on the lead having the field.
  const activityAgo = leadActivityAgo(activityAt);
  const activityOn = formatDisplayDateTime(activityAt);
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: lead.id, data: { lead }, disabled: !isInteractive });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  // Phase 105 Plan 105-08 (T-108-HOOKRULES): the hook above is always
  // called, but its drag attributes/listeners are only ever attached to the
  // DOM for an audience that may write — an external card, and (MKS-347) a
  // read-only internal one, must not expose any draggable affordance.
  const dragProps = isInteractive ? { ...attributes, ...listeners } : {};

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...dragProps}
      // MKS-347 — external still navigates to its own read-only detail page,
      // so only `readOnly` drops the click entirely: a marketing viewer has
      // no lead destination that is not a write surface.
      onClick={
        readOnly
          ? undefined
          : (e) => {
              e.stopPropagation();
              onClick(lead);
            }
      }
      className={`rounded-lg border bg-white p-3 shadow-sm transition-shadow ${
        readOnly ? '' : 'hover:shadow-md'
      } ${isInteractive ? 'cursor-grab' : ''} ${
        isStale ? 'border-red-500 border-2' : 'border-gray-200'
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <h4 className="text-sm font-medium text-gray-900 truncate">{lead.name}</h4>
        {isStale && (
          <span
            className="inline-flex h-2 w-2 shrink-0 rounded-full bg-red-500"
            // MKS-294: say WHY it is marked, so the dot is checkable instead
            // of mysterious.
            title={
              activityAgo ? `Stale — last activity ${activityAgo} (${activityOn})` : 'Stale lead'
            }
          />
        )}
      </div>
      <p className="mt-1 text-xs text-gray-500 truncate">{lead.customer?.name ?? 'No customer'}</p>
      {contact && (
        <p className="mt-0.5 text-xs text-gray-500 truncate" title={contact.name}>
          {contact.name}
        </p>
      )}
      {lead.labels.length > 0 && (
        <div className="mt-1.5 flex flex-wrap gap-1">
          {lead.labels.map((l) => (
            <span
              key={l.id}
              style={{ backgroundColor: l.color, color: labelTextColor(l.color) }}
              className="inline-block rounded px-1.5 py-0.5 text-[10px] font-medium leading-tight"
            >
              {l.name}
            </span>
          ))}
        </div>
      )}
      <div className="mt-2 text-sm font-semibold text-gray-700">
        {formatCurrency(dealValueCZK !== undefined ? String(dealValueCZK) : lead.dealValue)}
      </div>
      {/* MKS-346 — one badge per linked configuration, in the configuration's
          OWN currency: D-14 forbids converting between them, so there is
          deliberately no single combined figure here even when a lead links a
          EUR package and a CZK one. Not a link — the card is one big click
          target that opens the lead, and the modal is where the configurator
          link lives. */}
      {slaConfigurations.length > 0 && (
        <div className="mt-1 flex flex-wrap gap-1">
          {slaConfigurations.map((config) => (
            <span
              key={config.id}
              title={`SLA pricing: ${config.name}`}
              className="inline-block rounded bg-indigo-50 px-1.5 py-0.5 font-mono tabular-nums text-[10px] font-medium leading-tight text-indigo-700"
            >
              {formatMonthlyPrice(config.monthlyPrice, config.currency)}
            </span>
          ))}
        </div>
      )}
      {/* MKS-294: the owner line doubles as the activity line, so every card
          — stale or not — says when something last happened on it, and the
          red border stops being a mystery. No extra row: the relative age
          sits at the right end of a line the card already had. */}
      <div className="mt-1 flex items-baseline justify-between gap-2">
        <p className="text-xs text-gray-400 truncate" title={ownerName(lead.owner)}>
          {ownerName(lead.owner)}
        </p>
        {activityAgo && (
          <span
            className={`shrink-0 text-xs ${isStale ? 'text-red-500' : 'text-gray-400'}`}
            title={`Last activity: ${activityOn}`}
          >
            {activityAgo}
          </span>
        )}
      </div>
      {/* Phase 105 Plan 105-08 (T-108-WRITE): WonProjectStatus's primary
          path is a "Set up projects" button that opens a create/link
          mutation modal, and even its read links point at /projects/:id —
          an internal-only route the external viewer has no session for
          (mirrors the "names only, NOT links" precedent in
          pages/external/LeadDetailPage.tsx). Render nothing at all for
          external rather than a degraded/dead-end version. */}
      {/* MKS-347 — why the deal was lost, on the card, so a scan of the lost
          column answers "why are we losing?" without opening anything. Read
          off the data rather than a flag, like the SLA badge above: nothing
          can write a reason while the flag is off, so a card that has one is
          by definition past it. */}
      {lead.stageKind === 'lost' && lead.lostReason && (
        <div className="mt-2">
          <span
            title={lead.lostReasonNote ?? undefined}
            className="inline-block rounded bg-red-50 px-1.5 py-0.5 text-[10px] font-medium leading-tight text-red-700"
          >
            Lost: {lead.lostReason.name}
          </span>
        </div>
      )}
      {/* MKS-345 — the stage's KIND, not its name: a renamed won column
          still offers "Set up projects". */}
      {lead.stageKind === 'won' && isInteractive && <WonProjectStatus lead={lead} />}
      {lead.presalesProject && !isExternal && (
        <a
          href={`/projects/${lead.presalesProject.id}`}
          onClick={(e) => e.stopPropagation()}
          className="mt-2 flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-700"
        >
          Presales: {lead.presalesProject.name || `#${lead.presalesProject.id}`}
        </a>
      )}
    </div>
  );
}
